(() => {
  const ROOT = '[id^="HobbytechPartsFinder-"]';
  const MIN = 1, MAX = 5, STEP = .35, EPS = .001;
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

  function init(root) {
    if (!root || root.dataset.hpfInitialized) return;
    root.dataset.hpfInitialized = 'true';

    const tabs = [...root.querySelectorAll('[data-hpf-tab]')];
    const panels = [...root.querySelectorAll('[data-hpf-panel]')];
    const activate = (key, focus = false) => {
      tabs.forEach(tab => {
        const on = tab.dataset.hpfTab === key;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on);
        tab.tabIndex = on ? 0 : -1;
        if (on && focus) tab.focus();
      });
      panels.forEach(panel => {
        const on = panel.dataset.hpfPanel === key;
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
      });
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab.dataset.hpfTab));
      tab.addEventListener('keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();
        let n = i;
        if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') n = 0;
        if (e.key === 'End') n = tabs.length - 1;
        activate(tabs[n].dataset.hpfTab, true);
      });
    });

    root.querySelectorAll('[data-hpf-open-dialog]').forEach(button => {
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.hpfOpenDialog);
        if (dialog?.showModal) dialog.showModal();
      });
    });

    root.querySelectorAll('.hpf-dialog').forEach(dialog => {
      dialog.querySelectorAll('[data-hpf-close-dialog]').forEach(button =>
        button.addEventListener('click', () => dialog.close())
      );
      dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
      dialog.addEventListener('on:cart:add', () => dialog.close());
    });

    panels.forEach(panel => {
      const view = panel.querySelector('[data-hpf-viewport]');
      const stage = panel.querySelector('[data-hpf-stage]');
      if (!view || !stage) return;

      const zin = panel.querySelector('[data-hpf-zoom-in]');
      const zout = panel.querySelector('[data-hpf-zoom-out]');
      const resetButton = panel.querySelector('[data-hpf-reset]');
      const full = panel.querySelector('[data-hpf-fullscreen]');
      const hint = panel.querySelector('.hpf-viewer__hint');
      const image = stage.querySelector('img');
      const pointers = new Map();
      let scale = MIN, x = 0, y = 0, pan = null, pinch = null, suppress = 0;

      if (hint) hint.innerHTML = '<span aria-hidden="true">↕</span><span><strong>Pinch or scroll to zoom</strong><br>Drag to move around the diagram</span>';
      view.tabIndex = 0;
      view.setAttribute('role', 'region');
      view.setAttribute('aria-label', 'Interactive exploded diagram. On desktop, drag to move at any zoom level. Pinch or use the mouse wheel over the diagram to zoom.');
      Object.assign(view.style, { touchAction: 'pan-y', overscrollBehavior: 'contain', userSelect: 'none' });
      if (image) {
        image.draggable = false;
        image.style.userSelect = 'none';
        image.style.webkitUserDrag = 'none';
        image.addEventListener('dragstart', e => e.preventDefault());
      }

      const point = e => {
        const r = view.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };
      const center = () => ({ x: stage.offsetLeft + stage.offsetWidth / 2, y: stage.offsetTop + stage.offsetHeight / 2 });
      const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
      const canBasePan = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      function bound() {
        if (scale <= MIN + EPS) {
          scale = MIN;
          if (!canBasePan()) { x = 0; y = 0; return; }
          const minX = -stage.offsetLeft;
          const maxX = view.clientWidth - stage.offsetLeft - stage.offsetWidth;
          const minY = -stage.offsetTop;
          const maxY = view.clientHeight - stage.offsetTop - stage.offsetHeight;
          x = clamp(x, Math.min(minX, maxX), Math.max(minX, maxX));
          y = clamp(y, Math.min(minY, maxY), Math.max(minY, maxY));
          return;
        }
        const c = center();
        const hw = stage.offsetWidth * scale / 2;
        const hh = stage.offsetHeight * scale / 2;
        const vx = Math.min(96, Math.max(48, view.clientWidth * .12));
        const vy = Math.min(96, Math.max(48, view.clientHeight * .12));
        x = clamp(x, vx - c.x - hw, view.clientWidth - vx - c.x + hw);
        y = clamp(y, vy - c.y - hh, view.clientHeight - vy - c.y + hh);
      }

      function draw() {
        bound();
        stage.style.transform = `translate3d(${x}px,${y}px,0) scale(${scale})`;
        const inverseScale = 1 / scale;
        stage.style.setProperty('--hpf-mobile-hit-size', `${30 * inverseScale}px`);
        stage.style.setProperty('--hpf-mobile-dot-size', `${18 * inverseScale}px`);
        stage.style.setProperty('--hpf-mobile-dot-border', `${3 * inverseScale}px`);
        stage.style.setProperty('--hpf-mobile-pulse-size', `${28 * inverseScale}px`);
        const zoomed = scale > MIN + EPS;
        view.classList.toggle('is-zoomed', zoomed);
        view.style.touchAction = zoomed || pointers.size > 1 ? 'none' : 'pan-y';
        view.style.cursor = zoomed || canBasePan() ? 'grab' : 'default';
        if (zin) zin.disabled = scale >= MAX - EPS;
        if (zout) zout.disabled = scale <= MIN + EPS;
      }

      function zoom(next, at) {
        next = clamp(next, MIN, MAX);
        if (Math.abs(next - scale) < EPS) return;
        const wasZoomed = scale > MIN + EPS;
        const c = center();
        const cx = (at.x - c.x - x) / scale;
        const cy = (at.y - c.y - y) / scale;
        x = at.x - c.x - cx * next;
        y = at.y - c.y - cy * next;
        scale = next;
        if (wasZoomed && scale <= MIN + EPS) { x = 0; y = 0; }
        draw();
      }

      function reset() { scale = MIN; x = 0; y = 0; pan = pinch = null; view.classList.remove('is-dragging'); draw(); }
      const viewCenter = () => ({ x: view.clientWidth / 2, y: view.clientHeight / 2 });
      zin?.addEventListener('click', () => zoom(scale + STEP, viewCenter()));
      zout?.addEventListener('click', () => zoom(scale - STEP, viewCenter()));
      resetButton?.addEventListener('click', reset);
      full?.addEventListener('click', () => (view.requestFullscreen || view.webkitRequestFullscreen)?.call(view));

      view.addEventListener('wheel', e => {
        const wheelTarget = e.target instanceof Element ? e.target.closest('[data-hpf-stage]') : null;
        if (!wheelTarget) return;
        let d = e.deltaY;
        if (e.deltaMode === 1) d *= 16;
        if (e.deltaMode === 2) d *= view.clientHeight;
        const inward = d < 0;
        if ((!inward && scale <= MIN + EPS) || (inward && scale >= MAX - EPS)) return;
        e.preventDefault();
        zoom(scale * Math.exp(-d * .0015), point(e));
      }, { passive: false });

      function beginPinch() {
        if (pointers.size < 2) return;
        const [a, b] = [...pointers.values()].slice(0, 2);
        const m = midpoint(a, b), c = center();
        pinch = {
          distance: Math.max(1, distance(a, b)),
          scale,
          cx: (m.x - c.x - x) / scale,
          cy: (m.y - c.y - y) / scale
        };
        pan = null;
        view.classList.add('is-dragging');
      }

      view.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const hotspot = e.target.closest('.hpf-hotspot');
        const interactive = e.target.closest('.hpf-hotspot,button,a,input,select,textarea,label');
        if (e.pointerType === 'mouse' && interactive) return;
        const p = point(e);
        pointers.set(e.pointerId, { id: e.pointerId, type: e.pointerType, x: p.x, y: p.y });

        if (pointers.size > 1) {
          e.preventDefault();
          pointers.forEach(v => { try { view.setPointerCapture(v.id); } catch (_) {} });
          beginPinch();
          suppress = performance.now() + 350;
        } else {
          const touchPan = e.pointerType !== 'mouse' && scale > MIN + EPS && (!interactive || hotspot);
          const mousePan = e.pointerType === 'mouse' && (scale > MIN + EPS || canBasePan()) && !interactive;

          if (touchPan || mousePan) {
            const pending = e.pointerType !== 'mouse' && Boolean(hotspot);
            pan = { id: e.pointerId, px: p.x, py: p.y, x, y, pending };

            if (!pending) {
              e.preventDefault();
              try { view.setPointerCapture(e.pointerId); } catch (_) {}
              view.classList.add('is-dragging');
            }
          }
        }
      });

      view.addEventListener('pointermove', e => {
        if (!pointers.has(e.pointerId)) return;
        const p = point(e);
        pointers.set(e.pointerId, { id: e.pointerId, type: e.pointerType, x: p.x, y: p.y });

        if (pointers.size > 1) {
          e.preventDefault();
          if (!pinch) beginPinch();
          const [a, b] = [...pointers.values()].slice(0, 2);
          const m = midpoint(a, b), c = center();
          scale = clamp(pinch.scale * distance(a, b) / pinch.distance, MIN, MAX);
          x = m.x - c.x - pinch.cx * scale;
          y = m.y - c.y - pinch.cy * scale;
          suppress = performance.now() + 350;
          draw();
        } else if (pan?.id === e.pointerId) {
          const dx = p.x - pan.px, dy = p.y - pan.py;
          const travel = Math.hypot(dx, dy);

          if (pan.pending) {
            if (travel < 8) return;
            pan.pending = false;
            suppress = performance.now() + 300;
            try { view.setPointerCapture(e.pointerId); } catch (_) {}
            view.classList.add('is-dragging');
          }

          e.preventDefault();
          if (travel > 5) suppress = performance.now() + 250;
          x = pan.x + dx; y = pan.y + dy; draw();
        }
      });

      function end(e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.delete(e.pointerId);
        pinch = null;
        if (pointers.size === 1) {
          const p = pointers.values().next().value;
          if (scale > MIN + EPS || (p.type === 'mouse' && canBasePan())) {
            pan = { id: p.id, px: p.x, py: p.y, x, y };
          } else {
            pan = null;
            view.classList.remove('is-dragging');
          }
        } else {
          pan = null;
          view.classList.remove('is-dragging');
        }
        draw();
      }
      view.addEventListener('pointerup', end);
      view.addEventListener('pointercancel', end);
      view.addEventListener('lostpointercapture', end);
      view.addEventListener('click', e => {
        if (performance.now() < suppress) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);

      view.addEventListener('keydown', e => {
        if (e.key === '+' || e.key === '=') { e.preventDefault(); zoom(scale + STEP, viewCenter()); }
        else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoom(scale - STEP, viewCenter()); }
        else if (e.key === '0') { e.preventDefault(); reset(); }
      });

      new ResizeObserver(draw).observe(view);
      document.addEventListener('fullscreenchange', () => requestAnimationFrame(draw));
      if (image && !image.complete) image.addEventListener('load', draw, { once: true });
      draw();
    });

    root.querySelector('[data-hpf-scroll-products]')?.addEventListener('click', e => {
      e.preventDefault();
      const target = document.querySelector('.boost-sd__product-filter,.boost-sd__product-list,[class*="boost-sd__"],.cc-collection-products');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  document.querySelectorAll(ROOT).forEach(init);
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll(ROOT).forEach(init));
})();
